import { Business, EMPTY_SERVICE_SELL_COST, Service, ServiceSellCost, ServiceSellPrice, DEFAULT_CURRENCY } from "@turizi/shared";
import { useTranslation } from 'react-i18next';
import { ActionIcon, Button, Select, SelectProps, Modal, Tooltip } from '@mantine/core';
import { MoneyNumberInput } from '../snippets';
import { useState, useMemo } from 'react';
import { useCreateServiceSellCost } from "@/hooks/api-hooks";
import { notify } from "@/lib/notify";
import { formatDay, formatPrice, todayStart } from "@/lib/format-utils";
import { IconBuildingStore, IconCalendar, IconPlus, IconReportMoney } from "@tabler/icons-react";
import CurrencySelector from "../currency-selector";
import { TagIcon } from "@/lib/tag-utils";
import dayjs from "dayjs";


interface ServiceNewCostProps {
    service: Service;
    initialPrice?: ServiceSellPrice | null;
    compact?: boolean;
    onSave?: (service: Service) => void;
    onCancel?: () => void;
}

export default function ServiceNewCost({
    service,
    initialPrice,
    compact,
    onSave,
    onCancel,
}: ServiceNewCostProps) {
    const { t } = useTranslation();
    const today = todayStart();

    const [opened, setOpened] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [selectedPriceId, setSelectedPriceId] = useState<string | null>(null);
    const [draft, setDraft] = useState<ServiceSellCost>(EMPTY_SERVICE_SELL_COST);
    const [showForeignerCost, setShowForeignerCost] = useState(false);
    const createCostMutation = useCreateServiceSellCost();

    const sortedSellPrices = useMemo(() => {
        return [...(service?.sell_prices || [])]
            .filter(p => p.default_amount !== null && p.default_amount !== undefined)
            .sort((a, b) => {
                const dateA = a.start_day || a.end_day || '';
                const dateB = b.start_day || b.end_day || '';
                return dateB.localeCompare(dateA);
            });
    }, [service?.sell_prices]);

    const sellPriceMap = useMemo(() => {
        return new Map(sortedSellPrices.map((p, idx) => [(p.id ?? idx).toString(), p]));
    }, [sortedSellPrices]);

    const selectedSellPrice = useMemo(() => {
        if (!selectedPriceId) return null;
        return sellPriceMap.get(selectedPriceId) || null;
    }, [sellPriceMap, selectedPriceId]);

    const providerMap = useMemo(() => {
        return new Map((service?.providers || []).map((b: Business) => [b.id.toString(), b]));
    }, [service?.providers]);

    const selectedBusiness = useMemo(() => {
        if (!draft?.business_id) return null;
        return providerMap.get(draft.business_id.toString()) || null;
    }, [providerMap, draft?.business_id]);

    const renderProviderOption: SelectProps['renderOption'] = ({ option }) => {
        if (!option.value) {
            return (
                <div className="flex items-center gap-2 py-0.5">
                    <span className="text-gray-600 font-medium">{option.label}</span>
                </div>
            );
        }
        const b = providerMap.get(option.value);
        return (
            <div className="flex items-center gap-1.5 py-0.5 w-full">
                {b?.category && <TagIcon name={b.category} size="xxs" hideText />}
                <span className="font-medium text-sm text-gray-800">{option.label}</span>
                {b?.city && <span className="text-xs text-gray-400">{b.city}</span>}
                <span className="text-xxs text-gray-400">#{b?.id}</span>
            </div>
        );
    };

    const renderSellPriceOption: SelectProps['renderOption'] = ({ option }) => {
        const p = sellPriceMap.get(option.value);
        if (!p) return <span>{option.label}</span>;

        const isCurrent = (!p.start_day || p.start_day <= today) && (!p.end_day || p.end_day >= today);
        const isPast = p.end_day ? dayjs(p.end_day).isBefore(today, 'day') : false;
        const curr = p.default_currency || DEFAULT_CURRENCY;

        return (
            <div className="flex items-center justify-between gap-2 py-0.5 w-full text-xs">
                <div className="flex items-center gap-2">
                    <span
                        className={`w-2 h-2 rounded-full shrink-0 ${isCurrent ? 'bg-emerald-500' : isPast ? 'bg-gray-300' : 'bg-blue-500'}`}
                    />
                    <span className="font-semibold text-gray-900">
                        {p.start_day ? formatDay(p.start_day) : '—'} – {p.end_day ? formatDay(p.end_day) : '—'}
                    </span>
                </div>
                <span className="font-bold text-gray-700 tabular-nums">
                    {formatPrice(p.default_amount, { currency: curr })}
                </span>
            </div>
        );
    };

    const handleSelectSellPrice = (priceKey: string | null) => {
        setSelectedPriceId(priceKey);
        const priceObj = priceKey ? sellPriceMap.get(priceKey) : null;
        if (priceObj) {
            const curr = priceObj.default_currency || DEFAULT_CURRENCY;
            setDraft(prev => ({
                ...prev,
                start_day: priceObj.start_day || '',
                end_day: priceObj.end_day || '',
                default_currency: prev.default_currency || curr,
                child_currency: prev.child_currency || curr,
                senior_currency: prev.senior_currency || curr,
                infant_currency: prev.infant_currency || curr,
                foreigner_currency: prev.foreigner_currency || priceObj.foreigner_currency || curr,
            }));
            if (priceObj.foreigner_amount) {
                setShowForeignerCost(true);
            }
        } else {
            setDraft(prev => ({
                ...prev,
                start_day: '',
                end_day: '',
            }));
        }
    };

    const handleOpen = () => {
        const defaultPrice = initialPrice
            || sortedSellPrices.find(p => (!p.start_day || p.start_day <= today) && (!p.end_day || p.end_day >= today))
            || sortedSellPrices[0]
            || null;

        const defaultPriceKey = defaultPrice
            ? (defaultPrice.id ?? sortedSellPrices.indexOf(defaultPrice)).toString()
            : null;
        const curr = defaultPrice?.default_currency || DEFAULT_CURRENCY;

        setSelectedPriceId(defaultPriceKey);
        setShowForeignerCost(Boolean(defaultPrice?.foreigner_amount));
        setDraft({
            ...EMPTY_SERVICE_SELL_COST,
            business_id: service?.providers?.length === 1 ? service.providers[0].id : null,
            start_day: defaultPrice?.start_day || '',
            end_day: defaultPrice?.end_day || '',
            default_currency: curr,
            child_currency: curr,
            senior_currency: curr,
            infant_currency: curr,
            foreigner_currency: defaultPrice?.foreigner_currency || curr,
        });
        setOpened(true);
    };

    const save = async () => {
        if (!service?.id || !draft.business_id || !draft.start_day || !draft.end_day) return;

        createCostMutation.mutate({
            ...draft,
            service_id: service.id,
        }, {
            onSuccess: () => {
                notify.success(t('cost_saved_successfully'), '');

                if (service.linked_service_id) {
                    setShowConfirmDialog(true);
                } else {
                    setDraft(EMPTY_SERVICE_SELL_COST);
                    setOpened(false);
                    onSave?.(service);
                }
            }
        });
    };

    const confirmSaveToLinked = async () => {
        if (!service?.linked_service_id || !draft.business_id) return;

        createCostMutation.mutate({
            ...draft,
            service_id: service.linked_service_id,
        }, {
            onSuccess: () => {
                notify.success(t('saved_to_linked_service'), '');
                setDraft(EMPTY_SERVICE_SELL_COST);
                setShowConfirmDialog(false);
                setOpened(false);
                onSave?.(service);
            }
        });
    };

    const skipLinkedSave = () => {
        setDraft(EMPTY_SERVICE_SELL_COST);
        setShowConfirmDialog(false);
        setOpened(false);
        onSave?.(service);
    };

    const handleCancel = () => {
        setOpened(false);
        setDraft(EMPTY_SERVICE_SELL_COST);
        setShowForeignerCost(false);
        onCancel?.();
    };

    const renderTierMarginBadge = (
        priceAmount: number | null | undefined,
        priceCurrency: string | null | undefined,
        costAmount: number | null | undefined,
        costCurrency: string | null | undefined
    ) => {
        if (!selectedSellPrice) return null;
        const pCurr = priceCurrency || selectedSellPrice.default_currency || DEFAULT_CURRENCY;
        const cCurr = costCurrency || draft.default_currency || DEFAULT_CURRENCY;
        const hasPrice = priceAmount !== null && priceAmount !== undefined && priceAmount > 0;
        const hasCost = costAmount !== null && costAmount !== undefined && costAmount >= 0;

        return (
            <div className="items-center justify-between gap-1 pt-1 mt-1 border-t border-gray-200/80 text-[11px]">
                <span className="text-gray-500">
                    {t('sell_price')}:{' '}
                    <span className="font-semibold text-gray-800">
                        {hasPrice ? formatPrice(priceAmount, { currency: pCurr }) : '—'}
                    </span>
                </span>
                {hasPrice && hasCost && pCurr === cCurr && (
                    (() => {
                        const marginPct = 100 - (Number(costAmount) / Number(priceAmount)) * 100;
                        const hasLoss = Number(costAmount) > Number(priceAmount);
                        return (
                            <div
                                className={`text-[10px] w-fit font-semibold px-1.5 py-0.2 rounded tabular-nums ${hasLoss
                                    ? 'text-red-600 bg-red-50 border border-red-200'
                                    : 'text-emerald-700 bg-emerald-50 border border-emerald-200'
                                    }`}
                            >
                                {marginPct.toFixed(1)}%
                            </div>
                        );
                    })()
                )}
            </div>
        );
    };

    const costClassName = "p-2.5 bg-gray-50 rounded-lg flex flex-col justify-between border border-gray-100";

    const initialDateRangeLabel = initialPrice
        ? `${initialPrice.start_day ? formatDay(initialPrice.start_day) : '—'} – ${initialPrice.end_day ? formatDay(initialPrice.end_day) : '—'}`
        : '';

    return (
        <>
            {compact ? (
                <Tooltip label={`${t('add_cost')} (${initialDateRangeLabel})`} withArrow>
                    <ActionIcon
                        variant="subtle"
                        color="dark"
                        size="sm"
                        onClick={handleOpen}
                    >
                        <IconPlus size={14} stroke={1.8} />
                    </ActionIcon>
                </Tooltip>
            ) : (
                <Button
                    size="xs"
                    radius="sm"
                    color={opened ? 'orange' : 'black'}
                    leftSection={<IconReportMoney size={16} stroke={1} />}
                    onClick={handleOpen}
                >
                    {t('add_cost')}
                </Button>
            )}

            <Modal
                opened={opened}
                onClose={handleCancel}
                title={<div className="flex items-center gap-2">
                    <IconReportMoney size={24} stroke={2} />
                    <div className="text-xs font-bold">{t('new_cost')}</div>
                </div>}
                size="xl"
                radius="lg"
            >
                {showConfirmDialog ? (
                    <div className="space-y-4">
                        <div className="text-xl font-semibold flex items-center gap-2">
                            <IconReportMoney size={24} stroke={2} />
                            {t('save_to_linked_service')}
                        </div>
                        <p>{t('do_you_want_to_apply_this_cost_to_linked_service')}</p>
                        <div className="flex justify-end gap-2">
                            <Button
                                variant="subtle"
                                color="gray"
                                onClick={skipLinkedSave}
                            >
                                {t('no')}
                            </Button>
                            <Button
                                color="black"
                                onClick={confirmSaveToLinked}
                                loading={createCostMutation.isPending}
                            >
                                {t('yes')}
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {/* Provider & Sell Price Period Selection */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <Select
                                    label={t('provider')}
                                    placeholder={t('select_provider')}
                                    leftSection={
                                        selectedBusiness?.category
                                            ? <TagIcon name={selectedBusiness.category} size="xxs" hideText />
                                            : <IconBuildingStore size={16} stroke={1.5} />
                                    }
                                    value={draft?.business_id ? draft.business_id.toString() : null}
                                    data={
                                        service?.providers?.map((provider: Business) => ({
                                            value: provider.id.toString(),
                                            label: provider.name
                                        })) || []
                                    }
                                    onChange={(value) => setDraft(prev => (
                                        { ...prev, business_id: value ? Number(value) : null }
                                    ))}
                                    searchable
                                    required
                                    renderOption={renderProviderOption}
                                />
                                {(!service?.providers || service.providers.length === 0) && (
                                    <div className="text-xs text-red-500 mt-1">
                                        {t('no_providers_assigned')}
                                    </div>
                                )}
                            </div>

                            <div>
                                <Select
                                    label={t('sell_price')}
                                    placeholder={t('select_sell_price')}
                                    leftSection={<IconCalendar size={16} stroke={1.5} />}
                                    value={selectedPriceId}
                                    data={sortedSellPrices.map((p, idx) => {
                                        const key = (p.id ?? idx).toString();
                                        const curr = p.default_currency || DEFAULT_CURRENCY;
                                        return {
                                            value: key,
                                            label: `${p.start_day ? formatDay(p.start_day) : '—'} – ${p.end_day ? formatDay(p.end_day) : '—'} (${formatPrice(p.default_amount, { currency: curr })})`,
                                        };
                                    })}
                                    onChange={handleSelectSellPrice}
                                    renderOption={renderSellPriceOption}
                                    searchable
                                    required
                                />
                                {sortedSellPrices.length === 0 && (
                                    <div className="text-xs text-amber-600 mt-1">
                                        {t('create_sell_price_first')}
                                    </div>
                                )}
                            </div>
                        </div>

                        <CurrencySelector
                            value={draft?.default_currency || DEFAULT_CURRENCY}
                            onChange={currency => setDraft(prev => ({
                                ...prev,
                                default_currency: currency,
                                child_currency: currency,
                                senior_currency: currency,
                                infant_currency: currency,
                            }))}
                        />

                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2">
                            {/* Default Cost Input + Margin */}
                            <div className={costClassName}>
                                <MoneyNumberInput
                                    className="w-full"
                                    amount={draft?.default_amount}
                                    onChange={amount => setDraft(prev => ({ ...prev, default_amount: amount }))}
                                    label={t('default')}
                                />
                                {renderTierMarginBadge(
                                    selectedSellPrice?.default_amount,
                                    selectedSellPrice?.default_currency,
                                    draft?.default_amount,
                                    draft?.default_currency
                                )}
                            </div>

                            {/* Child Cost Input + Margin */}
                            <div className={costClassName}>
                                <MoneyNumberInput
                                    className="w-full"
                                    amount={draft?.child_amount}
                                    onChange={amount => setDraft(prev => ({ ...prev, child_amount: amount }))}
                                    label={t('children')}
                                />
                                {renderTierMarginBadge(
                                    selectedSellPrice?.child_amount,
                                    selectedSellPrice?.child_currency || selectedSellPrice?.default_currency,
                                    draft?.child_amount,
                                    draft?.child_currency || draft?.default_currency
                                )}
                            </div>

                            {/* Senior Cost Input + Margin */}
                            <div className={costClassName}>
                                <MoneyNumberInput
                                    className="w-full"
                                    amount={draft?.senior_amount}
                                    onChange={amount => setDraft(prev => ({ ...prev, senior_amount: amount }))}
                                    label={service.show_retired ? t('retired_plural') : t('seniors')}
                                />
                                {renderTierMarginBadge(
                                    selectedSellPrice?.senior_amount,
                                    selectedSellPrice?.senior_currency || selectedSellPrice?.default_currency,
                                    draft?.senior_amount,
                                    draft?.senior_currency || draft?.default_currency
                                )}
                            </div>

                            {/* Infant Cost Input + Margin */}
                            <div className={costClassName}>
                                <MoneyNumberInput
                                    className="w-full"
                                    amount={draft?.infant_amount}
                                    onChange={amount => setDraft(prev => ({ ...prev, infant_amount: amount }))}
                                    label={t('infants')}
                                />
                                {renderTierMarginBadge(
                                    selectedSellPrice?.infant_amount,
                                    selectedSellPrice?.infant_currency || selectedSellPrice?.default_currency,
                                    draft?.infant_amount,
                                    draft?.infant_currency || draft?.default_currency
                                )}
                            </div>
                        </div>

                        {showForeignerCost ? (
                            <div className="solid-box-border-blue space-y-2">
                                <div>
                                    {t('foreigner_cost')}
                                </div>
                                <CurrencySelector
                                    value={draft?.foreigner_currency || DEFAULT_CURRENCY}
                                    onChange={currency => setDraft(prev => ({
                                        ...prev,
                                        foreigner_currency: currency,
                                    }))}
                                />
                                <MoneyNumberInput
                                    className="flex-1"
                                    amount={draft?.foreigner_amount}
                                    onChange={amount => setDraft(prev => ({ ...prev, foreigner_amount: amount }))}
                                    label={t('cost') + ' * ' + t('foreigner')}
                                />
                                {renderTierMarginBadge(
                                    selectedSellPrice?.foreigner_amount,
                                    selectedSellPrice?.foreigner_currency || selectedSellPrice?.default_currency,
                                    draft?.foreigner_amount,
                                    draft?.foreigner_currency || draft?.default_currency
                                )}
                            </div>
                        ) : (
                            <div>
                                <Button
                                    variant="subtle"
                                    size="xs"
                                    radius="xl"
                                    leftSection={<IconPlus size={14} />}
                                    onClick={() => setShowForeignerCost(true)}
                                >
                                    {t('enter_different_foreigner_cost')}
                                </Button>
                            </div>
                        )}

                        <div className="flex justify-between gap-2 mt-6">
                            <Button
                                color="black"
                                variant="subtle"
                                size="xs"
                                onClick={handleCancel}
                            >
                                {t('cancel')}
                            </Button>
                            <Button
                                color="black"
                                size="xs"
                                radius="xl"
                                loading={createCostMutation.isPending}
                                onClick={save}
                                disabled={!selectedSellPrice || !draft?.start_day || !draft?.end_day || draft?.default_amount === null || draft?.default_amount === undefined || !draft?.business_id}
                            >
                                {t('save')}
                            </Button>
                        </div>

                    </div>
                )}
            </Modal>
        </>
    );
}